import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    financeText,
    getFinanceLanguage,
    useFinanceDocumentLang,
    saveFinanceLanguage,
} from "finance-language";

import { Alert, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem, Snackbar, Stack, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AddOutlined,
    CheckCircle,
    CreditCardOutlined,
    DeleteOutlineOutlined,
    EditOutlined,
    PaymentsOutlined,
    ReceiptLongOutlined,
    RefreshOutlined,
    SearchOutlined,
    WarningAmberRounded,
    WalletOutlined,
} from "@mui/icons-material";

import {
    FinanceKpiTile,
} from "../crm/invoice/finance/shared/FinanceUiKit";
// Koyu hero bandı için (yedekten geri yüklendi)
import { AccountBalanceOutlined } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";
/* =========================================================
   CONFIG
========================================================= */

const API_URL =
    process.env.REACT_APP_API_URL;

/*
 * Backend route:
 *
 * .use("/finance/cash", cashRoutes)
 *
 * Bu nedenle Company Cash hesapları:
 *
 * /api/finance/cash/accounts
 */
const CASH_API =
    "/finance/cash";

/* =========================================================
   CONSTANTS
========================================================= */

const CURRENCIES = [
    "TRY",
    "USD",
    "EUR",
    "GBP",
];

const emptyCardForm = {
    name: "",
    bankDetailId: "",
    bankName: "",
    cardHolder: "",
    lastFourDigits: "",
    currency: "TRY",
    cardLimit: "",
    statementDay: "",
    dueDay: "",
    linkedPaymentAccountId: "",
    description: "",
    isActive: true,
};

const emptyPurchaseForm = {
    amount: "",
    category: "",
    referenceNo: "",
    description: "",
};

const emptyPaymentForm = {
    accountId: "",
    amount: "",
    referenceNo: "",
    description: "",
};

/* =========================================================
   COLORS
========================================================= */

const COLORS = {
    navy: "#0B1F3A",

    blue: "#175CD3",
    blueLight: "#EFF8FF",

    green: "#027A48",
    greenLight: "#ECFDF3",

    red: "#B42318",
    redLight: "#FEF3F2",

    amber: "#B54708",
    amberLight: "#FFFAEB",

    purple: "#6941C6",
    purpleLight: "#F4F3FF",

    text: "#101828",
    subtext: "#667085",

    border: "#E4E7EC",
    page: "#F5F7FA",

    white: "#FFFFFF",
};

const cardSx = {
    borderRadius: "10px",

    background: COLORS.white,

    border: "1px solid rgba(15, 23, 42, 0.12)",

    boxShadow: "none",

    overflow: "hidden",
};

/* =========================================================
   BUTTONS
========================================================= */

const getIconButtonSx = (
    tone
) => {
    const configs = {
        purchase: {
            color:
            COLORS.blue,

            bg:
            COLORS.blueLight,

            border:
                "#B2DDFF",

            hover:
                "#D1E9FF",
        },

        payment: {
            color:
            COLORS.green,

            bg:
            COLORS.greenLight,

            border:
                "#ABEFC6",

            hover:
                "#D1FADF",
        },

        edit: {
            color:
            COLORS.purple,

            bg:
            COLORS.purpleLight,

            border:
                "#D9D6FE",

            hover:
                "#E9E7FD",
        },

        delete: {
            color:
            COLORS.red,

            bg:
            COLORS.redLight,

            border:
                "#FECDCA",

            hover:
                "#FEE4E2",
        },
    };

    const config =
        configs[tone] ||
        configs.purchase;

    return {
        width:
            36,

        height:
            36,

        color:
        config.color,

        background:
        config.bg,

        border:
            `1px solid ${config.border}`,

        borderRadius:
            "9px",

        "&:hover": {
            background:
            config.hover,
        },

        "&.Mui-disabled": {
            color:
                "#98A2B3",

            background:
                "#F2F4F7",

            border:
                `1px solid ${COLORS.border}`,
        },
    };
};

/* =========================================================
   HELPERS
========================================================= */

const normalizeText = (
    value
) =>
    String(
        value || ""
    ).trim();

const getId = (
    value
) => {
    if (!value) {
        return "";
    }

    if (
        typeof value ===
        "object"
    ) {
        return String(
            value._id ||
            value.id ||
            ""
        );
    }

    return String(
        value
    );
};

/* =========================================================
   NORMALIZE LIST

   Backend farklı response wrapper dönerse de
   mümkün olduğunca listeyi buluyoruz.
========================================================= */

const normalizeList = (
    payload
) => {
    let items =
        [];

    if (
        Array.isArray(
            payload
        )
    ) {
        items =
            payload;
    } else if (
        Array.isArray(
            payload?.data
        )
    ) {
        items =
            payload.data;
    } else if (
        Array.isArray(
            payload?.data?.data
        )
    ) {
        items =
            payload.data.data;
    } else if (
        Array.isArray(
            payload?.results
        )
    ) {
        items =
            payload.results;
    } else if (
        Array.isArray(
            payload?.cards
        )
    ) {
        items =
            payload.cards;
    } else if (
        Array.isArray(
            payload?.accounts
        )
    ) {
        items =
            payload.accounts;
    } else if (
        Array.isArray(
            payload?.bankDetails
        )
    ) {
        items =
            payload.bankDetails;
    }

    return items.map(
        (
            item
        ) => {
            if (
                item?.attributes
            ) {
                return {
                    _id:
                        item.id ||
                        item.attributes?._id,

                    ...item.attributes,
                };
            }

            return item;
        }
    );
};

/* =========================================================
   MONEY
========================================================= */

const formatMoney = (
    amount,
    currency = "TRY"
) => {
    try {
        return new Intl.NumberFormat(
            "tr-TR",
            {
                style:
                    "currency",

                currency,

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        ).format(
            Number(
                amount
            ) ||
            0
        );
    } catch {
        return `${
            Number(
                amount
            ) ||
            0
        } ${currency}`;
    }
};

/* =========================================================
   AVAILABLE LIMIT

   Öncelik backend virtual:
   card.availableLimit

   Yoksa fallback:
   cardLimit - currentDebt
========================================================= */

const calculateAvailableLimit = (
    card
) => {
    if (!card) {
        return 0;
    }

    const rawBackendAvailable =
        card.availableLimit;

    if (
        rawBackendAvailable !==
        undefined &&
        rawBackendAvailable !==
        null &&
        rawBackendAvailable !==
        ""
    ) {
        const backendAvailable =
            Number(
                rawBackendAvailable
            );

        if (
            Number.isFinite(
                backendAvailable
            )
        ) {
            return Math.max(
                backendAvailable,
                0
            );
        }
    }

    return Math.max(
        Number(
            card.cardLimit ||
            0
        ) -
        Number(
            card.currentDebt ||
            0
        ),

        0
    );
};

const getErrorMessage = (
    error,
    fallback =
        financeText("An unexpected error occurred.")
) =>
    error?.message ||
    fallback;

/* =========================================================
   METRIC CARD
========================================================= */

// Görünüm Finance kitindeki KPI kartıyla aynı (bkz. shared/FinanceUiKit.js)
const MetricCard = (props) => <FinanceKpiTile {...props} />;

/* =========================================================
   MAIN COMPONENT
========================================================= */

const CreditCards = () => {
    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    useFinanceDocumentLang(financeLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const token =
        localStorage.getItem(
            "token"
        );

    /* =====================================================
       DATA STATES
    ===================================================== */

    const [
        cards,
        setCards,
    ] = useState([]);

    const [
        cashAccounts,
        setCashAccounts,
    ] = useState([]);

    const [
        bankDetails,
        setBankDetails,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        summaryCurrency,
        setSummaryCurrency,
    ] = useState(
        "TRY"
    );

    const [
        lastUpdated,
        setLastUpdated,
    ] = useState(
        new Date()
    );

    /* =====================================================
       CARD CREATE / EDIT
    ===================================================== */

    const [
        cardModal,
        setCardModal,
    ] = useState(false);

    const [
        editingCard,
        setEditingCard,
    ] = useState(null);

    const [
        cardForm,
        setCardForm,
    ] = useState(
        emptyCardForm
    );

    /* =====================================================
       PURCHASE
    ===================================================== */

    const [
        purchaseModal,
        setPurchaseModal,
    ] = useState(false);

    const [
        purchaseCard,
        setPurchaseCard,
    ] = useState(null);

    const [
        purchaseForm,
        setPurchaseForm,
    ] = useState(
        emptyPurchaseForm
    );

    /* =====================================================
       PAYMENT
    ===================================================== */

    const [
        paymentModal,
        setPaymentModal,
    ] = useState(false);

    const [
        paymentCard,
        setPaymentCard,
    ] = useState(null);

    const [
        paymentForm,
        setPaymentForm,
    ] = useState(
        emptyPaymentForm
    );

    /* =====================================================
       DELETE
    ===================================================== */

    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState(null);

    /* =====================================================
       NOTIFICATION
    ===================================================== */

    const [
        notification,
        setNotification,
    ] = useState({
        open:
            false,

        message:
            "",

        severity:
            "success",
    });

    const showNotification =
        useCallback(
            (
                message,
                severity =
                    "success"
            ) => {
                setNotification({
                    open:
                        true,

                    message,

                    severity,
                });
            },

            []
        );

    const closeNotification =
        () => {
            setNotification(
                (
                    previous
                ) => ({
                    ...previous,

                    open:
                        false,
                })
            );
        };

    /* =====================================================
       API REQUEST
    ===================================================== */

    const apiRequest =
        useCallback(
            async (
                path,
                options = {}
            ) => {
                const baseUrl =
                    String(
                        API_URL ||
                        ""
                    ).replace(
                        /\/+$/,
                        ""
                    );

                const headers = {
                    "Content-Type":
                        "application/json",

                    ...(options.headers ||
                        {}),
                };

                if (token) {
                    headers.Authorization =
                        `Bearer ${token}`;
                }

                const response =
                    await fetch(
                        `${baseUrl}${path}`,

                        {
                            ...options,

                            headers,
                        }
                    );

                let body =
                    null;

                try {
                    body =
                        await response.json();
                } catch {
                    body =
                        null;
                }

                if (
                    !response.ok
                ) {
                    throw new Error(
                        body
                            ?.errors?.[0]
                            ?.detail ||
                        body
                            ?.message ||
                        `Request failed with status ${response.status}.`
                    );
                }

                return body;
            },

            [
                token,
            ]
        );

    /* =====================================================
       FETCH

       CREDIT CARDS:
       /api/credit-cards

       CASH ACCOUNTS:
       /api/finance/cash/accounts

       BANK DETAIL:
       /api/bank-detail
    ===================================================== */

    const fetchData =
        useCallback(
            async () => {
                setLoading(
                    true
                );

                try {
                    const results =
                        await Promise.allSettled(
                            [
                                apiRequest(
                                    "/credit-cards"
                                ),

                                apiRequest(
                                    `${CASH_API}/accounts`
                                ),

                                apiRequest(
                                    "/bank-detail"
                                ),
                            ]
                        );

                    const [
                        cardsResult,
                        accountsResult,
                        bankDetailsResult,
                    ] =
                        results;

                    /* =====================================
                       CARDS
                    ===================================== */

                    if (
                        cardsResult.status ===
                        "fulfilled"
                    ) {
                        setCards(
                            normalizeList(
                                cardsResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Credit cards error:",
                            cardsResult.reason
                        );

                        setCards(
                            []
                        );

                        showNotification(
                            financeText("Credit cards could not be loaded."),

                            "error"
                        );
                    }

                    /* =====================================
                       CASH ACCOUNTS
                    ===================================== */

                    if (
                        accountsResult.status ===
                        "fulfilled"
                    ) {
                        setCashAccounts(
                            normalizeList(
                                accountsResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Cash accounts error:",
                            accountsResult.reason
                        );

                        setCashAccounts(
                            []
                        );

                        showNotification(
                            financeText("Company Cash accounts could not be loaded."),

                            "error"
                        );
                    }

                    /* =====================================
                       BANK DETAILS
                    ===================================== */

                    if (
                        bankDetailsResult.status ===
                        "fulfilled"
                    ) {
                        setBankDetails(
                            normalizeList(
                                bankDetailsResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Bank details error:",
                            bankDetailsResult.reason
                        );

                        setBankDetails(
                            []
                        );
                    }

                    setLastUpdated(
                        new Date()
                    );
                } catch (
                    error
                    ) {
                    console.error(
                        "Credit Cards fetch error:",
                        error
                    );

                    showNotification(
                        getErrorMessage(
                            error,

                            financeText("Credit cards could not be loaded.")
                        ),

                        "error"
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },

            [
                apiRequest,
                showNotification,
            ]
        );

    useEffect(
        () => {
            fetchData();
        },

        [
            fetchData,
        ]
    );

    /* =====================================================
       SUMMARY BY CURRENCY
    ===================================================== */

    const currencyCards =
        useMemo(
            () =>
                cards.filter(
                    (
                        card
                    ) =>
                        card.currency ===
                        summaryCurrency
                ),

            [
                cards,
                summaryCurrency,
            ]
        );

    const summary =
        useMemo(
            () =>
                currencyCards.reduce(
                    (
                        result,
                        card
                    ) => {
                        const limit =
                            Number(
                                card.cardLimit
                            ) ||
                            0;

                        const debt =
                            Number(
                                card.currentDebt
                            ) ||
                            0;

                        const available =
                            calculateAvailableLimit(
                                card
                            );

                        result.limit +=
                            limit;

                        result.debt +=
                            debt;

                        result.available +=
                            available;

                        if (
                            card.isActive !==
                            false
                        ) {
                            result.active +=
                                1;
                        }

                        return result;
                    },

                    {
                        limit:
                            0,

                        debt:
                            0,

                        available:
                            0,

                        active:
                            0,
                    }
                ),

            [
                currencyCards,
            ]
        );

    const totalActiveCards =
        useMemo(
            () =>
                cards.filter(
                    (
                        card
                    ) =>
                        card.isActive !==
                        false
                ).length,

            [
                cards,
            ]
        );

    /* =====================================================
       FILTER
    ===================================================== */

    const filteredCards =
        useMemo(
            () => {
                const query =
                    search
                        .trim()
                        .toLowerCase();

                if (!query) {
                    return cards;
                }

                return cards.filter(
                    (
                        card
                    ) => {
                        const linkedAccount =
                            card
                                ?.linkedPaymentAccountId;

                        return [
                            card.name,
                            card.bankName,
                            card.cardHolder,
                            card.lastFourDigits,
                            card.currency,
                            linkedAccount?.name,
                            card.description,
                        ].some(
                            (
                                value
                            ) =>
                                String(
                                    value ||
                                    ""
                                )
                                    .toLowerCase()
                                    .includes(
                                        query
                                    )
                        );
                    }
                );
            },

            [
                cards,
                search,
            ]
        );

    /* =====================================================
       CARD FORM PAYMENT ACCOUNTS
    ===================================================== */

    const cardFormPaymentAccounts =
        useMemo(
            () =>
                cashAccounts.filter(
                    (
                        account
                    ) =>
                        account.isActive !==
                        false &&
                        account.currency ===
                        cardForm.currency
                ),

            [
                cashAccounts,
                cardForm.currency,
            ]
        );

    /* =====================================================
       PAYMENT ACCOUNTS
    ===================================================== */

    const paymentAccounts =
        useMemo(
            () => {
                if (
                    !paymentCard
                ) {
                    return [];
                }

                return cashAccounts.filter(
                    (
                        account
                    ) =>
                        account.isActive !==
                        false &&
                        account.currency ===
                        paymentCard.currency
                );
            },

            [
                cashAccounts,
                paymentCard,
            ]
        );

    const selectedPaymentAccount =
        useMemo(
            () =>
                paymentAccounts.find(
                    (
                        account
                    ) =>
                        getId(
                            account
                        ) ===
                        String(
                            paymentForm.accountId
                        )
                ) ||
                null,

            [
                paymentAccounts,
                paymentForm.accountId,
            ]
        );

    /* =====================================================
       PAYMENT PREVIEW
    ===================================================== */

    const paymentPreview =
        useMemo(
            () => {
                if (
                    !paymentCard
                ) {
                    return {
                        amount:
                            0,

                        remainingDebt:
                            0,

                        availableAfter:
                            0,

                        accountAfter:
                            null,

                        insufficientBalance:
                            false,

                        exceedsDebt:
                            false,
                    };
                }

                const amount =
                    Number(
                        paymentForm.amount ||
                        0
                    );

                const cardLimit =
                    Number(
                        paymentCard.cardLimit ||
                        0
                    );

                const currentDebt =
                    Number(
                        paymentCard.currentDebt ||
                        0
                    );

                const remainingDebt =
                    Math.max(
                        currentDebt -
                        amount,

                        0
                    );

                const availableAfter =
                    Math.max(
                        cardLimit -
                        remainingDebt,

                        0
                    );

                const accountAfter =
                    selectedPaymentAccount
                        ? Number(
                            selectedPaymentAccount.currentBalance ||
                            0
                        ) -
                        amount
                        : null;

                return {
                    amount,

                    remainingDebt,

                    availableAfter,

                    accountAfter,

                    insufficientBalance:
                        selectedPaymentAccount
                            ? amount >
                            Number(
                                selectedPaymentAccount.currentBalance ||
                                0
                            )
                            : false,

                    exceedsDebt:
                        amount >
                        currentDebt,
                };
            },

            [
                paymentCard,
                paymentForm.amount,
                selectedPaymentAccount,
            ]
        );

    /* =====================================================
       PURCHASE PREVIEW
    ===================================================== */

    const purchasePreview =
        useMemo(
            () => {
                if (
                    !purchaseCard
                ) {
                    return {
                        amount:
                            0,

                        currentDebt:
                            0,

                        newDebt:
                            0,

                        currentAvailable:
                            0,

                        newAvailable:
                            0,

                        exceedsLimit:
                            false,
                    };
                }

                const amount =
                    Number(
                        purchaseForm.amount ||
                        0
                    );

                const currentDebt =
                    Number(
                        purchaseCard.currentDebt ||
                        0
                    );

                const currentAvailable =
                    calculateAvailableLimit(
                        purchaseCard
                    );

                return {
                    amount,

                    currentDebt,

                    newDebt:
                        currentDebt +
                        amount,

                    currentAvailable,

                    newAvailable:
                        Math.max(
                            currentAvailable -
                            amount,

                            0
                        ),

                    exceedsLimit:
                        amount >
                        currentAvailable,
                };
            },

            [
                purchaseCard,
                purchaseForm.amount,
            ]
        );

    /* =====================================================
       ADD CARD
    ===================================================== */

    const openAddCard =
        () => {
            setEditingCard(
                null
            );

            setCardForm(
                emptyCardForm
            );

            setCardModal(
                true
            );
        };

    /* =====================================================
       EDIT CARD
    ===================================================== */

    const openEditCard =
        (
            card
        ) => {
            setEditingCard(
                card
            );

            setCardForm({
                name:
                    card.name ||
                    "",

                bankDetailId:
                    getId(
                        card.bankDetailId
                    ),

                bankName:
                    card.bankName ||
                    card.bankDetailId?.bankName ||
                    "",

                cardHolder:
                    card.cardHolder ||
                    "",

                lastFourDigits:
                    card.lastFourDigits ||
                    "",

                currency:
                    card.currency ||
                    "TRY",

                cardLimit:
                    card.cardLimit ??
                    "",

                statementDay:
                    card.statementDay ??
                    "",

                dueDay:
                    card.dueDay ??
                    "",

                linkedPaymentAccountId:
                    getId(
                        card.linkedPaymentAccountId
                    ),

                description:
                    card.description ||
                    "",

                isActive:
                    card.isActive !==
                    false,
            });

            setCardModal(
                true
            );
        };

    const closeCardModal =
        () => {
            if (
                saving
            ) {
                return;
            }

            setCardModal(
                false
            );

            setEditingCard(
                null
            );

            setCardForm(
                emptyCardForm
            );
        };

    /* =====================================================
       SAVE CARD
    ===================================================== */

    const saveCard =
        async () => {
            try {
                /* =========================================
                   NAME
                ========================================= */

                if (
                    !normalizeText(
                        cardForm.name
                    )
                ) {
                    showNotification(
                        financeText("Card name is required."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   LAST FOUR
                ========================================= */

                if (
                    !/^\d{4}$/.test(
                        String(
                            cardForm.lastFourDigits ||
                            ""
                        )
                    )
                ) {
                    showNotification(
                        financeText("Last 4 digits must contain exactly four numbers."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   LIMIT
                ========================================= */

                const cardLimit =
                    Number(
                        cardForm.cardLimit
                    );

                if (
                    String(
                        cardForm.cardLimit ??
                        ""
                    ).trim() ===
                    "" ||
                    !Number.isFinite(
                        cardLimit
                    ) ||
                    cardLimit <
                    0
                ) {
                    showNotification(
                        financeText("Card limit is invalid."),

                        "warning"
                    );

                    return;
                }

                /*
                 * Mevcut borcun altına limit düşürülemez.
                 *
                 * Örnek:
                 * Debt = 9.000
                 * Limit = 5.000 yapılamaz.
                 */

                if (
                    editingCard &&
                    cardLimit <
                    Number(
                        editingCard.currentDebt ||
                        0
                    )
                ) {
                    showNotification(
                        financeText("Card limit cannot be lower than current card debt."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   STATEMENT DAY
                ========================================= */

                const statementDay =
                    cardForm.statementDay ===
                    ""
                        ? null
                        : Number(
                            cardForm.statementDay
                        );

                const dueDay =
                    cardForm.dueDay ===
                    ""
                        ? null
                        : Number(
                            cardForm.dueDay
                        );

                if (
                    statementDay !==
                    null &&
                    (
                        !Number.isInteger(
                            statementDay
                        ) ||
                        statementDay <
                        1 ||
                        statementDay >
                        31
                    )
                ) {
                    showNotification(
                        financeText("Statement day must be between 1 and 31."),

                        "warning"
                    );

                    return;
                }

                if (
                    dueDay !==
                    null &&
                    (
                        !Number.isInteger(
                            dueDay
                        ) ||
                        dueDay <
                        1 ||
                        dueDay >
                        31
                    )
                ) {
                    showNotification(
                        financeText("Due day must be between 1 and 31."),

                        "warning"
                    );

                    return;
                }

                setSaving(
                    true
                );

                const payload = {
                    name:
                        normalizeText(
                            cardForm.name
                        ),

                    bankDetailId:
                        cardForm.bankDetailId ||
                        null,

                    bankName:
                        normalizeText(
                            cardForm.bankName
                        ),

                    cardHolder:
                        normalizeText(
                            cardForm.cardHolder
                        ),

                    lastFourDigits:
                        String(
                            cardForm.lastFourDigits
                        ),

                    currency:
                    cardForm.currency,

                    /*
                     * Sabit tanımlı limit.
                     * Harcama/ödeme bu değeri değiştirmez.
                     */
                    cardLimit,

                    statementDay,

                    dueDay,

                    linkedPaymentAccountId:
                        cardForm.linkedPaymentAccountId ||
                        null,

                    description:
                        normalizeText(
                            cardForm.description
                        ),

                    isActive:
                    cardForm.isActive,
                };

                /* =========================================
                   UPDATE
                ========================================= */

                if (
                    editingCard
                ) {
                    await apiRequest(
                        `/credit-cards/${getId(
                            editingCard
                        )}`,

                        {
                            method:
                                "PATCH",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("Credit card updated successfully.")
                    );
                } else {
                    /* =====================================
                       CREATE
                    ===================================== */

                    await apiRequest(
                        "/credit-cards",

                        {
                            method:
                                "POST",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("Credit card created successfully.")
                    );
                }

                setCardModal(
                    false
                );

                setEditingCard(
                    null
                );

                setCardForm(
                    emptyCardForm
                );

                await fetchData();
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("Credit card could not be saved.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       BANK CHANGE
    ===================================================== */

    const handleBankChange =
        (
            bankDetailId
        ) => {
            const bank =
                bankDetails.find(
                    (
                        item
                    ) =>
                        getId(
                            item
                        ) ===
                        String(
                            bankDetailId
                        )
                );

            setCardForm(
                (
                    previous
                ) => ({
                    ...previous,

                    bankDetailId,

                    bankName:
                        bank?.bankName ||
                        "",

                    currency:
                        editingCard &&
                        Number(
                            editingCard.currentDebt ||
                            0
                        ) >
                        0
                            ? previous.currency
                            : bank?.currency ||
                            previous.currency,

                    /*
                     * Currency değişebileceği için
                     * payment account temizlenir.
                     */
                    linkedPaymentAccountId:
                        "",
                })
            );
        };

    /* =====================================================
       CURRENCY CHANGE
    ===================================================== */

    const handleCardCurrencyChange =
        (
            value
        ) => {
            setCardForm(
                (
                    previous
                ) => ({
                    ...previous,

                    currency:
                    value,

                    linkedPaymentAccountId:
                        "",
                })
            );
        };

    /* =====================================================
       PURCHASE OPEN
    ===================================================== */

    const openPurchase =
        (
            card
        ) => {
            if (
                card.isActive ===
                false
            ) {
                showNotification(
                    financeText("Inactive credit card cannot be used."),

                    "warning"
                );

                return;
            }

            if (
                calculateAvailableLimit(
                    card
                ) <=
                0
            ) {
                showNotification(
                    financeText("Credit card has no available limit."),

                    "warning"
                );

                return;
            }

            setPurchaseCard(
                card
            );

            setPurchaseForm(
                emptyPurchaseForm
            );

            setPurchaseModal(
                true
            );
        };

    const closePurchase =
        () => {
            if (
                saving
            ) {
                return;
            }

            setPurchaseModal(
                false
            );

            setPurchaseCard(
                null
            );

            setPurchaseForm(
                emptyPurchaseForm
            );
        };

    /* =====================================================
       SAVE PURCHASE

       ÖRNEK:

       Limit:     10.000
       Debt:           0
       Available: 10.000

       Purchase:   9.000

       SONRA:

       Limit:     10.000
       Debt:       9.000
       Available:  1.000

       Company Cash değişmez.
    ===================================================== */

    const savePurchase =
        async () => {
            try {
                if (
                    !purchaseCard
                ) {
                    return;
                }

                if (
                    purchaseCard.isActive ===
                    false
                ) {
                    showNotification(
                        financeText("Inactive credit card cannot be used."),

                        "warning"
                    );

                    return;
                }

                const amount =
                    Number(
                        purchaseForm.amount
                    );

                if (
                    !Number.isFinite(
                        amount
                    ) ||
                    amount <=
                    0
                ) {
                    showNotification(
                        financeText("Purchase amount must be greater than zero."),

                        "warning"
                    );

                    return;
                }

                const available =
                    calculateAvailableLimit(
                        purchaseCard
                    );

                if (
                    amount >
                    available
                ) {
                    showNotification(
                        `${financeText("Purchase amount exceeds available card limit. Available")}: ${formatMoney(
                            available,
                            purchaseCard.currency
                        )}`,

                        "warning"
                    );

                    return;
                }

                setSaving(
                    true
                );

                await apiRequest(
                    `/credit-cards/${getId(
                        purchaseCard
                    )}/purchase`,

                    {
                        method:
                            "POST",

                        body:
                            JSON.stringify({
                                amount,

                                category:
                                    normalizeText(
                                        purchaseForm.category
                                    ),

                                referenceNo:
                                    normalizeText(
                                        purchaseForm.referenceNo
                                    ),

                                description:
                                    normalizeText(
                                        purchaseForm.description
                                    ),
                            }),
                    }
                );

                setPurchaseModal(
                    false
                );

                setPurchaseCard(
                    null
                );

                setPurchaseForm(
                    emptyPurchaseForm
                );

                /*
                 * Güncel currentDebt ve availableLimit
                 * backend'den tekrar çekilir.
                 */
                await fetchData();

                showNotification(
                    financeText("Credit card purchase recorded successfully.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("Credit card purchase could not be recorded.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       PAYMENT OPEN
    ===================================================== */

    const openPayment =
        (
            card
        ) => {
            const debt =
                Number(
                    card.currentDebt ||
                    0
                );

            if (
                card.isActive ===
                false
            ) {
                showNotification(
                    financeText("Inactive credit card cannot be paid."),

                    "warning"
                );

                return;
            }

            if (
                debt <=
                0
            ) {
                showNotification(
                    financeText("Credit card has no outstanding debt."),

                    "warning"
                );

                return;
            }

            setPaymentCard(
                card
            );

            /*
             * Kartın default bağlı hesabı varsa
             * ödeme popup'ına otomatik gelir.
             */
            setPaymentForm({
                ...emptyPaymentForm,

                accountId:
                    getId(
                        card.linkedPaymentAccountId
                    ),
            });

            setPaymentModal(
                true
            );
        };

    const closePayment =
        () => {
            if (
                saving
            ) {
                return;
            }

            setPaymentModal(
                false
            );

            setPaymentCard(
                null
            );

            setPaymentForm(
                emptyPaymentForm
            );
        };

    /* =====================================================
       PAY CARD DEBT

       ÖRNEK:

       Card Limit:        10.000
       Current Debt:       9.000
       Available:          1.000

       Cash Account:      50.000

       Payment:            9.000

       SONRA:

       Card Limit:        10.000
       Current Debt:           0
       Available:         10.000

       Cash Account:      41.000
    ===================================================== */

    const savePayment =
        async () => {
            try {
                if (
                    !paymentCard
                ) {
                    return;
                }

                if (
                    paymentCard.isActive ===
                    false
                ) {
                    showNotification(
                        financeText("Inactive credit card cannot be paid."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   ACCOUNT
                ========================================= */

                if (
                    !paymentForm.accountId
                ) {
                    showNotification(
                        financeText("Please select a payment account."),

                        "warning"
                    );

                    return;
                }

                if (
                    !selectedPaymentAccount
                ) {
                    showNotification(
                        financeText("Selected payment account could not be found."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   AMOUNT
                ========================================= */

                const amount =
                    Number(
                        paymentForm.amount
                    );

                if (
                    !Number.isFinite(
                        amount
                    ) ||
                    amount <=
                    0
                ) {
                    showNotification(
                        financeText("Payment amount must be greater than zero."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   DEBT CHECK
                ========================================= */

                const currentDebt =
                    Number(
                        paymentCard.currentDebt ||
                        0
                    );

                if (
                    amount >
                    currentDebt
                ) {
                    showNotification(
                        `${financeText("Payment cannot exceed current card debt. Current debt")}: ${formatMoney(
                            currentDebt,
                            paymentCard.currency
                        )}`,

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   ACCOUNT BALANCE
                ========================================= */

                const accountBalance =
                    Number(
                        selectedPaymentAccount.currentBalance ||
                        0
                    );

                if (
                    amount >
                    accountBalance
                ) {
                    showNotification(
                        `${financeText("Selected account has insufficient balance. Current balance")}: ${formatMoney(
                            accountBalance,
                            selectedPaymentAccount.currency
                        )}`,

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   CURRENCY
                ========================================= */

                if (
                    selectedPaymentAccount.currency !==
                    paymentCard.currency
                ) {
                    showNotification(
                        financeText("Credit card and payment account currencies must match."),

                        "warning"
                    );

                    return;
                }

                setSaving(
                    true
                );

                /*
                 * POST
                 * /api/credit-cards/:id/pay
                 *
                 * Backend aynı anda:
                 *
                 * cashAccount.currentBalance -= amount
                 * card.currentDebt -= amount
                 *
                 * yapıyor.
                 */

                await apiRequest(
                    `/credit-cards/${getId(
                        paymentCard
                    )}/pay`,

                    {
                        method:
                            "POST",

                        body:
                            JSON.stringify({
                                accountId:
                                paymentForm.accountId,

                                amount,

                                referenceNo:
                                    normalizeText(
                                        paymentForm.referenceNo
                                    ),

                                description:
                                    normalizeText(
                                        paymentForm.description
                                    ),
                            }),
                    }
                );

                setPaymentModal(
                    false
                );

                setPaymentCard(
                    null
                );

                setPaymentForm(
                    emptyPaymentForm
                );

                /*
                 * Burada hem:
                 *
                 * /credit-cards
                 * /finance/cash/accounts
                 *
                 * tekrar çekilir.
                 *
                 * Bu nedenle kart available limit
                 * ve hesap bakiyesi aynı anda güncellenir.
                 */
                await fetchData();

                showNotification(
                    financeText("Credit card debt payment completed successfully.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("Credit card payment could not be completed.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       DELETE
    ===================================================== */

    const confirmDelete =
        async () => {
            if (
                !deleteTarget
            ) {
                return;
            }

            try {
                if (
                    Number(
                        deleteTarget.currentDebt ||
                        0
                    ) >
                    0
                ) {
                    showNotification(
                        financeText("A credit card with outstanding debt cannot be deleted."),

                        "warning"
                    );

                    return;
                }

                setSaving(
                    true
                );

                await apiRequest(
                    `/credit-cards/${getId(
                        deleteTarget
                    )}`,

                    {
                        method:
                            "DELETE",
                    }
                );

                setDeleteTarget(
                    null
                );

                await fetchData();

                showNotification(
                    financeText("Credit card deleted successfully.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("Credit card could not be deleted.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       TABLE DATA
    ===================================================== */

    const tableData =
        useMemo(
            () => ({
                columns: [
                    /* =====================================
                       CARD
                    ===================================== */

                    {
                        Header:
                            financeText("CARD"),

                        accessor:
                            "name",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            return (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1.1}
                                    py={0.35}
                                >
                                    <MDBox
                                        width={42}
                                        height={42}
                                        minWidth={42}
                                        borderRadius="8px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            color:
                                            COLORS.blue,

                                            background:
                                            COLORS.blueLight,

                                            border:
                                                "1px solid #B2DDFF",
                                        }}
                                    >
                                        <CreditCardOutlined />
                                    </MDBox>

                                    <MDBox
                                        minWidth={0}
                                    >
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {card.name ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,

                                                fontFamily:
                                                    "monospace",
                                            }}
                                        >
                                            **** **** ****{" "}
                                            {card.lastFourDigits ||
                                                "----"}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       BANK
                    ===================================== */

                    {
                        Header:
                            financeText("BANK"),

                        accessor:
                            "bankName",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            const bankName =
                                card.bankName ||
                                card.bankDetailId?.bankName ||
                                "-";

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {bankName}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {card.cardHolder ||
                                            financeText("No card holder")}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       CURRENCY
                    ===================================== */

                    {
                        Header:
                            financeText("CURRENCY"),

                        accessor:
                            "currency",

                        Cell: ({
                                   value,
                               }) => (
                            <Chip
                                size="small"
                                label={
                                    value ||
                                    "-"
                                }
                                sx={{
                                    color:
                                    COLORS.blue,

                                    background:
                                    COLORS.blueLight,

                                    border:
                                        "1px solid #B2DDFF",

                                    fontWeight:
                                        800,

                                    minWidth:
                                        56,
                                }}
                            />
                        ),
                    },

                    /* =====================================
                       TOTAL CARD LIMIT
                    ===================================== */

                    {
                        Header:
                            financeText("CARD LIMIT"),

                        accessor:
                            "cardLimit",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,

                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    {formatMoney(
                                        card.cardLimit,

                                        card.currency
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       CURRENT DEBT
                    ===================================== */

                    {
                        Header:
                            financeText("CURRENT DEBT"),

                        accessor:
                            "currentDebt",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            const debt =
                                Number(
                                    card.currentDebt
                                ) ||
                                0;

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            debt >
                                            0
                                                ? COLORS.red
                                                : COLORS.green,

                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    {formatMoney(
                                        debt,

                                        card.currency
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       AVAILABLE LIMIT
                    ===================================== */

                    {
                        Header:
                            financeText("AVAILABLE LIMIT"),

                        id:
                            "available",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            const available =
                                calculateAvailableLimit(
                                    card
                                );

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            available >
                                            0
                                                ? COLORS.green
                                                : COLORS.red,

                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    {formatMoney(
                                        available,

                                        card.currency
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       STATEMENT / DUE
                    ===================================== */

                    {
                        Header:
                            financeText("STATEMENT / DUE"),

                        id:
                            "days",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.text,

                                            fontWeight:
                                                700,
                                        }}
                                    >
                                        {financeText("Statement")}:{" "}
                                        {card.statementDay ||
                                            "-"}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Due")}:{" "}
                                        {card.dueDay ||
                                            "-"}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       {financeText("PAYMENT ACCOUNT")}
                    ===================================== */

                    {
                        Header:
                            financeText("PAYMENT ACCOUNT"),

                        id:
                            "paymentAccount",

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            let account =
                                card.linkedPaymentAccountId;

                            /*
                             * Backend populate etmediyse local
                             * CashAccount listesinden çöz.
                             */
                            if (
                                account &&
                                typeof account !==
                                "object"
                            ) {
                                account =
                                    cashAccounts.find(
                                        (
                                            item
                                        ) =>
                                            getId(
                                                item
                                            ) ===
                                            String(
                                                account
                                            )
                                    ) ||
                                    null;
                            }

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                                account
                                                    ? COLORS.text
                                                    : COLORS.subtext,
                                        }}
                                    >
                                        {account?.name ||
                                            financeText("Not Linked")}
                                    </MDTypography>

                                    {account && (
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency ||
                                                card.currency
                                            )}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       STATUS
                    ===================================== */

                    {
                        Header:
                            financeText("STATUS"),

                        accessor:
                            "isActive",

                        Cell: ({
                                   value,
                               }) => (
                            <Chip
                                size="small"
                                label={financeText(
                                    value !==
                                    false
                                        ? "Active"
                                        : "Inactive"
                                )}
                                sx={{
                                    color:
                                        value !==
                                        false
                                            ? COLORS.green
                                            : COLORS.red,

                                    background:
                                        value !==
                                        false
                                            ? COLORS.greenLight
                                            : COLORS.redLight,

                                    border:
                                        value !==
                                        false
                                            ? "1px solid #ABEFC6"
                                            : "1px solid #FECDCA",

                                    fontWeight:
                                        700,
                                }}
                            />
                        ),
                    },

                    /* =====================================
                       ACTIONS
                    ===================================== */

                    {
                        Header:
                            financeText("ACTIONS"),

                        id:
                            "actions",

                        disableSortBy:
                            true,

                        Cell: ({
                                   row,
                               }) => {
                            const card =
                                row.original;

                            const debt =
                                Number(
                                    card.currentDebt
                                ) ||
                                0;

                            const available =
                                calculateAvailableLimit(
                                    card
                                );

                            const active =
                                card.isActive !==
                                false;

                            return (
                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={0.55}
                                >
                                    {/* PURCHASE */}

                                    <Tooltip
                                        title={
                                            !active
                                                ? financeText("Inactive card cannot be used")
                                                : available >
                                                0
                                                    ? financeText("Record credit card purchase")
                                                    : financeText("Card has no available limit")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !active ||
                                                    available <=
                                                    0
                                                }
                                                onClick={() =>
                                                    openPurchase(
                                                        card
                                                    )
                                                }
                                                sx={getIconButtonSx(
                                                    "purchase"
                                                )}
                                            >
                                                <ReceiptLongOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* PAY DEBT */}

                                    <Tooltip
                                        title={
                                            !active
                                                ? financeText("Inactive card cannot be paid")
                                                : debt >
                                                0
                                                    ? financeText("Pay credit card debt")
                                                    : financeText("No outstanding debt")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !active ||
                                                    debt <=
                                                    0
                                                }
                                                onClick={() =>
                                                    openPayment(
                                                        card
                                                    )
                                                }
                                                sx={getIconButtonSx(
                                                    "payment"
                                                )}
                                            >
                                                <PaymentsOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* EDIT */}

                                    <Tooltip
                                        title={financeText("Edit credit card")}
                                    arrow
                                    >
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            openEditCard(
                                                card
                                            )
                                        }
                                        sx={getIconButtonSx(
                                            "edit"
                                        )}
                                    >
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                            {/* DELETE */}

                            <Tooltip
                                title={
                                    debt ===
                                    0
                                        ? financeText("Delete credit card")
                                        : financeText("Card with debt cannot be deleted")
                                }
                                arrow
                            >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    debt >
                                                    0
                                                }
                                                onClick={() =>
                                                    setDeleteTarget(
                                                        card
                                                    )
                                                }
                                                sx={getIconButtonSx(
                                                    "delete"
                                                )}
                                            >
                                                <DeleteOutlineOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                            </Tooltip>
                        </MDBox>
                        );
                        },
                    },
                ],

                rows:
                filteredCards,
            }),

            [
                filteredCards,
                cashAccounts,
                financeLanguage,
            ]
        );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight:
                        "100vh",

                    background:
                    COLORS.page,

                    mx: {
                        xs:
                            -2,

                        md:
                            -3,
                    },

                    px: {
                        xs:
                            2,

                        md:
                            3,
                    },
                }}
            >
                {/* =================================================
                    HERO
                ================================================= */}

                <Card
                    sx={{
                        borderRadius:
                            "22px",

                        color:
                        COLORS.white,

                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",

                        boxShadow:
                            "0 18px 46px rgba(11, 31, 58, 0.22)",

                        overflow:
                            "hidden",

                        position:
                            "relative",

                        mb:
                            3,

                        "&::after": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                360,

                            height:
                                360,

                            borderRadius:
                                "50%",

                            right:
                                -130,

                            top:
                                -180,

                            background:
                                "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                230,

                            height:
                                230,

                            borderRadius:
                                "50%",

                            right:
                                120,

                            bottom:
                                -170,

                            background:
                                "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{
                            xs:
                                2.5,

                            md:
                                3.5,
                        }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            {/* LEFT */}

                            <Grid
                                item
                                xs={12}
                                lg={7}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background:
                                                "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <CreditCardOutlined
                                            sx={{
                                                color:
                                                    "#FFFFFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",

                                            letterSpacing:
                                                ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE TREASURY")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "#FFFFFF",

                                        lineHeight:
                                            1.12,
                                    }}
                                >
                                    {financeText("Corporate Credit Cards")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        maxWidth:
                                            760,
                                    }}
                                >
                                    {financeText(
                                        "Manage company credit cards, fixed card limits, available spending limits, outstanding debt, purchases and payments through Company Cash."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${totalActiveCards} ${financeText("active cards")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <AccountBalanceOutlined
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${cashAccounts.length} ${financeText("Company Cash accounts")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color:
                                                        "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString(
                                            [],
                                            {
                                                hour:
                                                    "2-digit",

                                                minute:
                                                    "2-digit",
                                            }
                                        )}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            {/* RIGHT */}

                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.09)",

                                        border:
                                            "1px solid rgba(255,255,255,.13)",

                                        backdropFilter:
                                            "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <MDBox>
                                            <MDTypography
                                                variant="caption"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                        "rgba(255,255,255,.72)",
                                                }}
                                            >
                                                {financeText("CURRENT CARD DEBT")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                mt={0.3}
                                                sx={{
                                                    color:
                                                        "#B2DDFF",

                                                    fontWeight:
                                                        700,
                                                }}
                                            >
                                                {
                                                    summaryCurrency
                                                }
                                            </MDTypography>
                                        </MDBox>

                                        <CreditCardOutlined
                                            sx={{
                                                color:
                                                    "#B2DDFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        mt={0.7}
                                        sx={{
                                            color:
                                                "#FFFFFF",
                                        }}
                                    >
                                        {formatMoney(
                                            summary.debt,

                                            summaryCurrency
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.5}
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText("Total Limit")}{" "}
                                        <strong>
                                            {formatMoney(
                                                summary.limit,

                                                summaryCurrency
                                            )}
                                        </strong>

                                        {" · "}

                                        {financeText("Available")}{" "}
                                        <strong>
                                            {formatMoney(
                                                summary.available,

                                                summaryCurrency
                                            )}
                                        </strong>
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        gap={1}
                                        mt={2}
                                        alignItems="stretch"
                                    >
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={
                                                openAddCard
                                            }
                                            sx={{
                                                background:
                                                    "#FFFFFF !important",

                                                color:
                                                    `${COLORS.navy} !important`,

                                                flex:
                                                    1,

                                                boxShadow:
                                                    "none",
                                            }}
                                        >
                                            <AddOutlined
                                                sx={{
                                                    mr:
                                                        0.5,
                                                }}
                                            />

                                            {financeText("Add Credit Card")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh")}>
                                            <span style={{ display: "inline-flex" }}><IconButton
                                                onClick={
                                                    fetchData
                                                }
                                                disabled={
                                                    loading
                                                }
                                                sx={{
                                                    color:
                                                        "#FFFFFF",

                                                    border:
                                                        "1px solid rgba(255,255,255,.18)",

                                                    borderRadius:
                                                        "10px",
                                                }}
                                            >
                                                {loading ? (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            color:
                                                                "#FFFFFF",
                                                        }}
                                                    />
                                                ) : (
                                                    <RefreshOutlined />
                                                )}
                                            </IconButton></span>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* =================================================
                    CURRENCY SELECTOR
                ================================================= */}

                <MDBox
                    display="flex"
                    justifyContent={{
                        xs:
                            "stretch",

                        sm:
                            "flex-end",
                    }}
                    mb={1.5}
                >
                    <TextField
                        select
                        size="small"
                        label={financeText("Summary Currency")}
                        value={
                            summaryCurrency
                        }
                        onChange={(
                            event
                        ) =>
                            setSummaryCurrency(
                                event.target.value
                            )
                        }
                        sx={{
                            width: {
                                xs:
                                    "100%",

                                sm:
                                    210,
                            },

                            "& .MuiOutlinedInput-root":
                                {
                                    borderRadius:
                                        "11px",

                                    background:
                                        "#FFFFFF",
                                },
                        }}
                    >
                        {CURRENCIES.map(
                            (
                                item
                            ) => (
                                <MenuItem
                                    key={
                                        item
                                    }
                                    value={
                                        item
                                    }
                                >
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </MDBox>

                {/* =================================================
                    METRICS
                ================================================= */}

                <Grid
                    container
                    spacing={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Active Cards")}
                            value={
                                summary.active
                            }
                            subtitle={`${summaryCurrency} ${financeText("corporate cards")}`}
                            icon={
                                <CreditCardOutlined />
                            }
                            tone="navy"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Total Limit")}
                            value={formatMoney(
                                summary.limit,

                                summaryCurrency
                            )}
                            subtitle={financeText("Fixed bank-defined credit limit")}
                            icon={
                                <WalletOutlined />
                            }
                            tone="blue"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Current Debt")}
                            value={formatMoney(
                                summary.debt,

                                summaryCurrency
                            )}
                            subtitle={financeText("Outstanding card debt")}
                            icon={
                                <PaymentsOutlined />
                            }
                            tone={
                                summary.debt >
                                0
                                    ? "red"
                                    : "green"
                            }
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Available Limit")}
                            value={formatMoney(
                                summary.available,

                                summaryCurrency
                            )}
                            subtitle={financeText("Total limit minus current debt")}
                            icon={
                                <CheckCircle />
                            }
                            tone="green"
                        />
                    </Grid>
                </Grid>

                {/* =================================================
                    DATATABLE
                ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mt:
                            2.5,
                    }}
                >
                    <MDBox
                        p={3}
                    >
                        {/* HEADER */}

                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1.5}
                            mb={2.5}
                        >
                            <MDBox>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Credit Cards")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        "Manage corporate cards, purchases, available limits, debt and Company Cash payments."
                                    )}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                display="flex"
                                gap={1}
                            >
                                <MDButton
                                    variant="contained"
                                    onClick={
                                        openAddCard
                                    }
                                    sx={{
                                        background:
                                            `${COLORS.blue} !important`,

                                        color:
                                            "#FFFFFF !important",

                                        borderRadius:
                                            "10px",

                                        boxShadow:
                                            "none",
                                    }}
                                >
                                    <AddOutlined
                                        sx={{
                                            mr:
                                                0.6,
                                        }}
                                    />

                                    {financeText("Add Credit Card")}
                                </MDButton>

                                <MDButton
                                    variant="outlined"
                                    color="dark"
                                    onClick={
                                        fetchData
                                    }
                                    disabled={
                                        loading
                                    }
                                    sx={{
                                        borderRadius:
                                            "10px",
                                    }}
                                >
                                    <RefreshOutlined
                                        sx={{
                                            mr:
                                                0.6,
                                        }}
                                    />

                                    {financeText("Refresh")}
                                </MDButton>
                            </MDBox>
                        </MDBox>

                        {/* SEARCH */}

                        <TextField
                            fullWidth
                            size="small"
                            label={financeText("Search")}
                            placeholder={financeText("Card name, bank, holder, last four digits, currency...")}
                            value={
                                search
                            }
                            onChange={(
                                event
                            ) =>
                                setSearch(
                                    event.target.value
                                )
                            }
                            InputProps={{
                                startAdornment:
                                    (
                                        <InputAdornment position="start">
                                            <SearchOutlined
                                                fontSize="small"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root":
                                    {
                                        borderRadius:
                                            "11px",
                                    },
                            }}
                        />

                        {/* RESULT INFO */}

                        <MDBox
                            mt={2}
                            p={1.5}
                            borderRadius="8px"
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1}
                            sx={{
                                background:
                                    "#F9FAFB",

                                border:
                                    `1px solid ${COLORS.border}`,
                            }}
                        >
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Showing")}{" "}
                                <strong
                                    style={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        filteredCards.length
                                    }
                                </strong>{" "}
                                {financeText("of")}{" "}
                                <strong
                                    style={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        cards.length
                                    }
                                </strong>{" "}
                                {financeText("credit cards")}
                            </MDTypography>

                            <MDBox
                                display="flex"
                                gap={0.8}
                                flexWrap="wrap"
                            >
                                <Chip
                                    size="small"
                                    label={`${financeText("Active")} ${totalActiveCards}`}
                                    sx={{
                                        color:
                                        COLORS.green,

                                        background:
                                        COLORS.greenLight,

                                        border:
                                            "1px solid #ABEFC6",

                                        fontWeight:
                                            700,
                                    }}
                                />

                                <Chip
                                    size="small"
                                    label={`${financeText("With Debt")} ${
                                        cards.filter(
                                            (
                                                item
                                            ) =>
                                                Number(
                                                    item.currentDebt
                                                ) >
                                                0
                                        ).length
                                    }`}
                                    sx={{
                                        color:
                                        COLORS.red,

                                        background:
                                        COLORS.redLight,

                                        border:
                                            "1px solid #FECDCA",

                                        fontWeight:
                                            700,
                                    }}
                                />
                            </MDBox>
                        </MDBox>

                        {/* TABLE */}

                        <MDBox
                            mt={2.5}
                            sx={{
                                "& .MuiTableContainer-root":
                                    {
                                        border:
                                            `1px solid ${COLORS.border}`,

                                        borderRadius:
                                            "10px",

                                        overflowX:
                                            "auto",

                                        boxShadow:
                                            "0 5px 18px rgba(16,24,40,.04)",
                                    },

                                "& table":
                                    {
                                        minWidth:
                                            1500,
                                    },

                                "& table thead":
                                    {
                                        background:
                                            "#F8FAFC",
                                    },

                                "& table thead th":
                                    {
                                        color:
                                            "#475467 !important",

                                        fontSize:
                                            "10px !important",

                                        fontWeight:
                                            "800 !important",

                                        letterSpacing:
                                            ".08em",

                                        paddingTop:
                                            "15px !important",

                                        paddingBottom:
                                            "15px !important",
                                    },

                                "& table tbody td":
                                    {
                                        paddingTop:
                                            "13px !important",

                                        paddingBottom:
                                            "13px !important",

                                        verticalAlign:
                                            "middle",
                                    },

                                "& table tbody tr:hover":
                                    {
                                        background:
                                            "#F5F8FF !important",

                                        boxShadow:
                                            "inset 3px 0 0 #175CD3",
                                    },
                            }}
                        >
                            {loading ? (
                                <MDBox
                                    py={8}
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <CircularProgress />
                                </MDBox>
                            ) : (
                                <DataTable
                                    table={
                                        tableData
                                    }
                                    canSearch={
                                        false
                                    }
                                    entriesPerPage={{
                                        defaultValue:
                                            10,

                                        entries: [
                                            5,
                                            10,
                                            25,
                                            50,
                                            100,
                                        ],
                                    }}
                                    showTotalEntries
                                    isSorted
                                    noEndBorder
                                />
                            )}
                        </MDBox>
                    </MDBox>
                </Card>
            </MDBox>

            {/* =====================================================
                ADD / EDIT CREDIT CARD
            ===================================================== */}

            <Dialog
                open={
                    cardModal
                }
                onClose={
                    closeCardModal
                }
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1.1}
                    >
                        <MDBox
                            width={44}
                            height={44}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                    editingCard
                                        ? COLORS.purple
                                        : COLORS.blue,

                                background:
                                    editingCard
                                        ? COLORS.purpleLight
                                        : COLORS.blueLight,

                                border:
                                    editingCard
                                        ? "1px solid #D9D6FE"
                                        : "1px solid #B2DDFF",
                            }}
                        >
                            {editingCard ? (
                                <EditOutlined />
                            ) : (
                                <CreditCardOutlined />
                            )}
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText(
                                    editingCard
                                        ? "Edit Credit Card"
                                        : "Add Credit Card"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    editingCard
                                        ? "Update corporate credit card information."
                                        : "Register a new corporate credit card."
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <Grid
                        container
                        spacing={2}
                        mt={0}
                    >
                        {/* NAME */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Card Name")}
                                placeholder={financeText("Garanti Corporate Card")}
                                value={
                                    cardForm.name
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            name:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* BANK */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Bank")}
                                value={
                                    cardForm.bankDetailId
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleBankChange(
                                        event.target.value
                                    )
                                }
                            >
                                <MenuItem value="">
                                    {financeText("No Bank Selected")}
                                </MenuItem>

                                {bankDetails.map(
                                    (
                                        bank
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    bank
                                                )
                                            }
                                            value={
                                                getId(
                                                    bank
                                                )
                                            }
                                        >
                                            {bank.bankName ||
                                                financeText("Bank")}

                                            {" · "}

                                            {bank.currency ||
                                                "-"}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* HOLDER */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Card Holder")}
                                placeholder={financeText("Company / Employee Name")}
                                value={
                                    cardForm.cardHolder
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            cardHolder:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* LAST FOUR */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Last 4 Digits")}
                                placeholder="4582"
                                value={
                                    cardForm.lastFourDigits
                                }
                                inputProps={{
                                    maxLength:
                                        4,

                                    inputMode:
                                        "numeric",
                                }}
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            lastFourDigits:
                                                event.target.value
                                                    .replace(
                                                        /\D/g,
                                                        ""
                                                    )
                                                    .slice(
                                                        0,
                                                        4
                                                    ),
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* CURRENCY */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Currency")}
                                value={
                                    cardForm.currency
                                }
                                disabled={
                                    Boolean(
                                        editingCard &&
                                        Number(
                                            editingCard.currentDebt ||
                                            0
                                        ) >
                                        0
                                    )
                                }
                                helperText={
                                    editingCard &&
                                    Number(
                                        editingCard.currentDebt ||
                                        0
                                    ) >
                                    0
                                        ? financeText("Locked while the card has debt.")
                                        : undefined
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleCardCurrencyChange(
                                        event.target.value
                                    )
                                }
                            >
                                {CURRENCIES.map(
                                    (
                                        item
                                    ) => (
                                        <MenuItem
                                            key={
                                                item
                                            }
                                            value={
                                                item
                                            }
                                        >
                                            {item}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* CARD LIMIT */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                label={financeText("Card Limit")}
                                value={
                                    cardForm.cardLimit
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            cardLimit:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputProps={{
                                    startAdornment:
                                        (
                                            <InputAdornment position="start">
                                                {
                                                    cardForm.currency
                                                }
                                            </InputAdornment>
                                        ),
                                }}
                                helperText={
                                    editingCard
                                        ? `Must be at least ${formatMoney(
                                            editingCard.currentDebt ||
                                            0,

                                            editingCard.currency ||
                                            cardForm.currency
                                        )}`
                                        : financeText("Fixed bank-defined credit limit.")
                                }
                            />
                        </Grid>

                        {/* CURRENT DEBT */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Current Debt")}
                                value={
                                    editingCard
                                        ? formatMoney(
                                            editingCard.currentDebt,

                                            editingCard.currency
                                        )
                                        : formatMoney(
                                            0,

                                            cardForm.currency
                                        )
                                }
                                disabled
                                helperText={financeText("Debt only changes through purchases and payments.")}
                            />
                        </Grid>

                        {/* AVAILABLE PREVIEW */}

                        {editingCard && (
                            <Grid
                                item
                                xs={12}
                            >
                                <Alert
                                    severity="info"
                                    sx={{
                                        borderRadius:
                                            "8px",
                                    }}
                                >
                                    {financeText("Current Available Limit")}:{" "}
                                    <strong>
                                        {formatMoney(
                                            calculateAvailableLimit(
                                                editingCard
                                            ),

                                            editingCard.currency
                                        )}
                                    </strong>

                                    {" · "}

                                    {financeText("Available after new limit")}:{" "}
                                    <strong>
                                        {formatMoney(
                                            Math.max(
                                                Number(
                                                    cardForm.cardLimit ||
                                                    0
                                                ) -
                                                Number(
                                                    editingCard.currentDebt ||
                                                    0
                                                ),

                                                0
                                            ),

                                            cardForm.currency
                                        )}
                                    </strong>
                                </Alert>
                            </Grid>
                        )}

                        {/* STATEMENT */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                label={financeText("Statement Day")}
                                value={
                                    cardForm.statementDay
                                }
                                inputProps={{
                                    min:
                                        1,

                                    max:
                                        31,
                                }}
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            statementDay:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* DUE */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                label={financeText("Due Day")}
                                value={
                                    cardForm.dueDay
                                }
                                inputProps={{
                                    min:
                                        1,

                                    max:
                                        31,
                                }}
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            dueDay:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* PAYMENT ACCOUNT */}

                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Linked Payment Account")}
                                value={
                                    cardForm.linkedPaymentAccountId
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            linkedPaymentAccountId:
                                            event.target.value,
                                        })
                                    )
                                }
                                helperText={`Only active ${cardForm.currency} Company Cash accounts are shown.`}
                            >
                                <MenuItem value="">
                                    {financeText("No Default Payment Account")}
                                </MenuItem>

                                {cardFormPaymentAccounts.map(
                                    (
                                        account
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    account
                                                )
                                            }
                                            value={
                                                getId(
                                                    account
                                                )
                                            }
                                        >
                                            {
                                                account.name
                                            }

                                            {" · "}

                                            {financeText(
                                                account.type ===
                                                "cash"
                                                    ? "Cash"
                                                    : "Bank"
                                            )}

                                            {" · "}

                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency
                                            )}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* DESCRIPTION */}

                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={financeText("Description")}
                                value={
                                    cardForm.description
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCardForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            description:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        <Grid
                            item
                            xs={12}
                        >
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    "Card Limit is the fixed bank-defined limit. Purchases increase Current Debt and reduce Available Limit. Paying card debt reduces Company Cash and restores the Available Limit."
                                )}
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closeCardModal
                        }
                        sx={{
                            borderRadius:
                                "10px",
                        }}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            saveCard
                        }
                        sx={{
                            background:
                                `${COLORS.blue} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText(
                            editingCard
                                ? "Update Credit Card"
                                : "Add Credit Card"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                PURCHASE
            ===================================================== */}

            <Dialog
                open={
                    purchaseModal
                }
                onClose={
                    closePurchase
                }
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                COLORS.blue,

                                background:
                                COLORS.blueLight,

                                border:
                                    "1px solid #B2DDFF",
                            }}
                        >
                            <ReceiptLongOutlined />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText("Credit Card Purchase")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    "Record a company credit card purchase without affecting Company Cash."
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    {purchaseCard && (
                        <Stack
                            spacing={2}
                            mt={1}
                        >
                            {/* CARD SUMMARY */}

                            <MDBox
                                p={2}
                                borderRadius="8px"
                                sx={{
                                    background:
                                        "#F9FAFB",

                                    border:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        purchaseCard.name
                                    }
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    ****{" "}
                                    {
                                        purchaseCard.lastFourDigits
                                    }

                                    {" · "}

                                    {
                                        purchaseCard.currency
                                    }
                                </MDTypography>

                                <Grid
                                    container
                                    spacing={1}
                                    mt={0.5}
                                >
                                    <Grid
                                        item
                                        xs={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Card Limit")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {formatMoney(
                                                purchaseCard.cardLimit,

                                                purchaseCard.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Current Debt")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                    Number(
                                                        purchaseCard.currentDebt
                                                    ) >
                                                    0
                                                        ? COLORS.red
                                                        : COLORS.green,
                                            }}
                                        >
                                            {formatMoney(
                                                purchaseCard.currentDebt,

                                                purchaseCard.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Available Limit")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.green,
                                            }}
                                        >
                                            {formatMoney(
                                                calculateAvailableLimit(
                                                    purchaseCard
                                                ),

                                                purchaseCard.currency
                                            )}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>

                            {/* AMOUNT */}

                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                label={financeText("Purchase Amount")}
                                value={
                                    purchaseForm.amount
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPurchaseForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            amount:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputProps={{
                                    startAdornment:
                                        (
                                            <InputAdornment position="start">
                                                {
                                                    purchaseCard.currency
                                                }
                                            </InputAdornment>
                                        ),
                                }}
                            />

                            {/* CATEGORY */}

                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Category")}
                                placeholder={financeText("Hotel, office expense, airline...")}
                                value={
                                    purchaseForm.category
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPurchaseForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            category:
                                            event.target.value,
                                        })
                                    )
                                }
                            />

                            {/* REFERENCE */}

                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Reference No")}
                                value={
                                    purchaseForm.referenceNo
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPurchaseForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            referenceNo:
                                            event.target.value,
                                        })
                                    )
                                }
                            />

                            {/* DESCRIPTION */}

                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={financeText("Description")}
                                value={
                                    purchaseForm.description
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPurchaseForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            description:
                                            event.target.value,
                                        })
                                    )
                                }
                            />

                            {/* PREVIEW */}

                            {purchasePreview.amount >
                                0 && (
                                    <Alert
                                        severity={
                                            purchasePreview.exceedsLimit
                                                ? "error"
                                                : "info"
                                        }
                                        sx={{
                                            borderRadius:
                                                "8px",
                                        }}
                                    >
                                        <Grid
                                            container
                                            spacing={1.5}
                                        >
                                            <Grid
                                                item
                                                xs={12}
                                                sm={4}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Card Limit")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                        COLORS.text,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        purchaseCard.cardLimit,

                                                        purchaseCard.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                sm={4}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Debt After Purchase")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                        COLORS.red,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        purchasePreview.newDebt,

                                                        purchaseCard.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                sm={4}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Available After Purchase")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            purchasePreview.exceedsLimit
                                                                ? COLORS.red
                                                                : COLORS.green,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        purchasePreview.newAvailable,

                                                        purchaseCard.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>
                                        </Grid>

                                        {purchasePreview.exceedsLimit && (
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                mt={1}
                                                sx={{
                                                    color:
                                                    COLORS.red,

                                                    fontWeight:
                                                        700,
                                                }}
                                            >
                                                {financeText("Purchase amount exceeds the available card limit.")}
                                            </MDTypography>
                                        )}
                                    </Alert>
                                )}

                            <Alert
                                severity="warning"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    "This purchase increases Current Debt but does not decrease Company Cash. Company Cash changes when the credit card debt is actually paid."
                                )}
                            </Alert>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closePurchase
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving ||
                            purchasePreview.exceedsLimit
                        }
                        onClick={
                            savePurchase
                        }
                        sx={{
                            background:
                                `${COLORS.blue} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Record Purchase")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                PAY CREDIT CARD DEBT
            ===================================================== */}

            <Dialog
                open={
                    paymentModal
                }
                onClose={
                    closePayment
                }
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                COLORS.green,

                                background:
                                COLORS.greenLight,

                                border:
                                    "1px solid #ABEFC6",
                            }}
                        >
                            <PaymentsOutlined />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText("Pay Credit Card Debt")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    "Deduct payment from Company Cash and restore the credit card available limit."
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    {paymentCard && (
                        <Stack
                            spacing={2}
                            mt={1}
                        >
                            {/* =================================================
                                CARD CURRENT STATE
                            ================================================= */}

                            <MDBox
                                p={2}
                                borderRadius="8px"
                                sx={{
                                    background:
                                        "#F9FAFB",

                                    border:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        paymentCard.name
                                    }
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    ****{" "}
                                    {
                                        paymentCard.lastFourDigits
                                    }

                                    {" · "}

                                    {
                                        paymentCard.currency
                                    }
                                </MDTypography>

                                <Grid
                                    container
                                    spacing={1.5}
                                    mt={0.5}
                                >
                                    {/* CARD LIMIT */}

                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Card Limit")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {formatMoney(
                                                paymentCard.cardLimit,

                                                paymentCard.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    {/* CURRENT DEBT */}

                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Current Debt")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.red,
                                            }}
                                        >
                                            {formatMoney(
                                                paymentCard.currentDebt,

                                                paymentCard.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    {/* AVAILABLE */}

                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Available Limit")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.green,
                                            }}
                                        >
                                            {formatMoney(
                                                calculateAvailableLimit(
                                                    paymentCard
                                                ),

                                                paymentCard.currency
                                            )}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>

                            {/* =================================================
                                {financeText("PAYMENT ACCOUNT")}
                            ================================================= */}

                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Payment Account")}
                                value={
                                    paymentForm.accountId
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPaymentForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            accountId:
                                            event.target.value,
                                        })
                                    )
                                }
                                helperText={`Only active ${paymentCard.currency} Company Cash accounts are shown.`}
                            >
                                {paymentAccounts.length ===
                                    0 && (
                                        <MenuItem
                                            value=""
                                            disabled
                                        >
                                            {financeText("No suitable Company Cash account")}
                                        </MenuItem>
                                    )}

                                {paymentAccounts.map(
                                    (
                                        account
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    account
                                                )
                                            }
                                            value={
                                                getId(
                                                    account
                                                )
                                            }
                                        >
                                            {
                                                account.name
                                            }

                                            {" · "}

                                            {financeText(
                                                account.type ===
                                                "cash"
                                                    ? "Cash"
                                                    : "Bank"
                                            )}

                                            {" · "}

                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency
                                            )}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>

                            {/* =================================================
                                PAYMENT AMOUNT
                            ================================================= */}

                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                label={financeText("Payment Amount")}
                                value={
                                    paymentForm.amount
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPaymentForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            amount:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputProps={{
                                    startAdornment:
                                        (
                                            <InputAdornment position="start">
                                                {
                                                    paymentCard.currency
                                                }
                                            </InputAdornment>
                                        ),
                                }}
                            />

                            {/* =================================================
                                PAY FULL DEBT SHORTCUT
                            ================================================= */}

                            <MDBox
                                display="flex"
                                justifyContent="flex-end"
                            >
                                <MDButton
                                    variant="outlined"
                                    color="dark"
                                    size="small"
                                    onClick={() =>
                                        setPaymentForm(
                                            (
                                                previous
                                            ) => ({
                                                ...previous,

                                                amount:
                                                    String(
                                                        Number(
                                                            paymentCard.currentDebt ||
                                                            0
                                                        )
                                                    ),
                                            })
                                        )
                                    }
                                >
                                    {financeText("Pay Full Debt")}
                                </MDButton>
                            </MDBox>

                            {/* REFERENCE */}

                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Reference No")}
                                value={
                                    paymentForm.referenceNo
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPaymentForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            referenceNo:
                                            event.target.value,
                                        })
                                    )
                                }
                            />

                            {/* DESCRIPTION */}

                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={financeText("Description")}
                                value={
                                    paymentForm.description
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPaymentForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            description:
                                            event.target.value,
                                        })
                                    )
                                }
                            />

                            {/* =================================================
                                PAYMENT ACCOUNT SUMMARY
                            ================================================= */}

                            {selectedPaymentAccount && (
                                <MDBox
                                    p={2}
                                    borderRadius="8px"
                                    sx={{
                                        background:
                                        COLORS.blueLight,

                                        border:
                                            "1px solid #B2DDFF",
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    >
                                        {financeText("PAYMENT ACCOUNT")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        mt={0.5}
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {
                                            selectedPaymentAccount.name
                                        }
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Current Balance")}:{" "}
                                        <strong>
                                            {formatMoney(
                                                selectedPaymentAccount.currentBalance,

                                                selectedPaymentAccount.currency
                                            )}
                                        </strong>
                                    </MDTypography>
                                </MDBox>
                            )}

                            {/* =================================================
                                AFTER PAYMENT PREVIEW
                            ================================================= */}

                            {selectedPaymentAccount &&
                                paymentPreview.amount >
                                0 && (
                                    <MDBox
                                        p={2}
                                        borderRadius="8px"
                                        sx={{
                                            background:
                                                paymentPreview.insufficientBalance ||
                                                paymentPreview.exceedsDebt
                                                    ? COLORS.redLight
                                                    : COLORS.greenLight,

                                            border:
                                                paymentPreview.insufficientBalance ||
                                                paymentPreview.exceedsDebt
                                                    ? "1px solid #FECDCA"
                                                    : "1px solid #ABEFC6",
                                        }}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                    paymentPreview.insufficientBalance ||
                                                    paymentPreview.exceedsDebt
                                                        ? COLORS.red
                                                        : COLORS.green,
                                            }}
                                        >
                                            {financeText("AFTER PAYMENT")}
                                        </MDTypography>

                                        <Grid
                                            container
                                            spacing={1.5}
                                            mt={0.3}
                                        >
                                            {/* ACCOUNT BALANCE */}

                                            <Grid
                                                item
                                                xs={12}
                                                md={4}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Company Cash Balance")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            paymentPreview.accountAfter >=
                                                            0
                                                                ? COLORS.green
                                                                : COLORS.red,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        paymentPreview.accountAfter,

                                                        selectedPaymentAccount.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>

                                            {/* DEBT */}

                                            <Grid
                                                item
                                                xs={12}
                                                md={4}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Card Debt")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            paymentPreview.remainingDebt >
                                                            0
                                                                ? COLORS.red
                                                                : COLORS.green,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        paymentPreview.remainingDebt,

                                                        paymentCard.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>

                                            {/* AVAILABLE LIMIT */}

                                            <Grid
                                                item
                                                xs={12}
                                                md={4}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Available Limit")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                        COLORS.green,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        paymentPreview.availableAfter,

                                                        paymentCard.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                )}

                            {/* =================================================
                                VALIDATION ERRORS
                            ================================================= */}

                            {paymentPreview.exceedsDebt && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius:
                                            "8px",
                                    }}
                                >
                                    Payment cannot exceed current card debt {financeText("of")}{" "}
                                    <strong>
                                        {formatMoney(
                                            paymentCard.currentDebt,

                                            paymentCard.currency
                                        )}
                                    </strong>
                                    .
                                </Alert>
                            )}

                            {paymentPreview.insufficientBalance && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius:
                                            "8px",
                                    }}
                                >
                                    {financeText(
                                        "Selected Company Cash account does not have sufficient balance."
                                    )}
                                </Alert>
                            )}

                            {/* ACCOUNTING INFO */}

                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    "Paying the card decreases Company Cash by the payment amount, decreases Current Debt by the same amount and automatically restores the Available Limit. The fixed Card Limit does not change."
                                )}
                            </Alert>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closePayment
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving ||
                            !paymentForm.accountId ||
                            paymentPreview.amount <=
                            0 ||
                            paymentPreview.exceedsDebt ||
                            paymentPreview.insufficientBalance
                        }
                        onClick={
                            savePayment
                        }
                        sx={{
                            background:
                                `${COLORS.green} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Pay Credit Card")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                DELETE
            ===================================================== */}

            <Dialog
                open={Boolean(
                    deleteTarget
                )}
                onClose={() => {
                    if (
                        !saving
                    ) {
                        setDeleteTarget(
                            null
                        );
                    }
                }}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        textAlign:
                            "center",

                        pt:
                            4,
                    }}
                >
                    <MDBox
                        width={58}
                        height={58}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                            background:
                            COLORS.redLight,
                        }}
                    >
                        <WarningAmberRounded
                            sx={{
                                color:
                                COLORS.red,

                                fontSize:
                                    30,
                            }}
                        />
                    </MDBox>

                    <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color:
                            COLORS.text,
                        }}
                    >
                        {financeText("Delete Credit Card?")}
                    </MDTypography>

                    <MDTypography
                        variant="body2"
                        mt={1}
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        <strong>
                            {
                                deleteTarget?.name
                            }
                        </strong>

                        {" "}

                        ****
                        {
                            deleteTarget?.lastFourDigits
                        }

                        {" "}

                        {financeText("will be permanently deleted.")}
                    </MDTypography>

                    {deleteTarget && (
                        <MDBox
                            mt={2}
                            p={1.5}
                            borderRadius="8px"
                            sx={{
                                background:
                                    "#F9FAFB",

                                border:
                                    `1px solid ${COLORS.border}`,
                            }}
                        >
                            <Grid
                                container
                                spacing={1}
                            >
                                <Grid
                                    item
                                    xs={4}
                                >
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Limit")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        {formatMoney(
                                            deleteTarget.cardLimit,

                                            deleteTarget.currency
                                        )}
                                    </MDTypography>
                                </Grid>

                                <Grid
                                    item
                                    xs={4}
                                >
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Debt")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.red,
                                        }}
                                    >
                                        {formatMoney(
                                            deleteTarget.currentDebt,

                                            deleteTarget.currency
                                        )}
                                    </MDTypography>
                                </Grid>

                                <Grid
                                    item
                                    xs={4}
                                >
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Available")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.green,
                                        }}
                                    >
                                        {formatMoney(
                                            calculateAvailableLimit(
                                                deleteTarget
                                            ),

                                            deleteTarget.currency
                                        )}
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        </MDBox>
                    )}

                    <Alert
                        severity="warning"
                        sx={{
                            mt:
                                2,

                            textAlign:
                                "left",

                            borderRadius:
                                "8px",
                        }}
                    >
                        {financeText(
                            "A credit card can only be deleted when its current debt is zero."
                        )}
                    </Alert>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={() =>
                            setDeleteTarget(
                                null
                            )
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            confirmDelete
                        }
                        sx={{
                            background:
                                `${COLORS.red} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Delete Credit Card")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                NOTIFICATION
            ===================================================== */}

            <Snackbar
                open={
                    notification.open
                }
                autoHideDuration={
                    4500
                }
                onClose={
                    closeNotification
                }
                anchorOrigin={{
                    vertical:
                        "bottom",

                    horizontal:
                        "right",
                }}
            >
                <Alert
                    severity={
                        notification.severity
                    }
                    variant="filled"
                    onClose={
                        closeNotification
                    }
                    sx={{
                        width:
                            "100%",

                        borderRadius:
                            "8px",
                    }}
                >
                    {
                        notification.message
                    }
                </Alert>
            </Snackbar>

            <Footer />
        </DashboardLayout>
    );
};

export default CreditCards;